import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { CalendarDays, Car, MapPin, DollarSign, AlertTriangle } from "lucide-react";
import heroImage from "@/assets/gallery/aerial-river-cabanas.jpg";

const SLUG = "river-road-parking-guide-new-braunfels";
const CANONICAL = `/blog/${SLUG}`;
const TITLE = "River Road Parking Guide, New Braunfels — Lots, Fees & The Easy Way | Son's";
const DESCRIPTION =
  "Where to park on River Road in New Braunfels, what public lots cost, why offsite shuttle lots fill early, and how onsite cabana parking at Son's Guadalupe avoids the whole mess.";
const PUBLISH_DATE = "May 30, 2026";

const FAQS = [
  {
    q: "Where can you park on River Road in New Braunfels?",
    a: "There are a mix of City of New Braunfels public lots, private outfitter lots, and a few roadside pull-offs. Public lots near popular access points (like Hueco Springs and Rockin' R) fill up by mid-morning on summer weekends.",
  },
  {
    q: "How much does River Road parking cost?",
    a: "City public lots typically run $10–$20 per car per day. Private outfitter lots are bundled with tubing packages and usually cost more on busy weekends. Holiday weekends can run higher.",
  },
  {
    q: "Why do River Road parking lots fill up so early?",
    a: "Tubing demand is concentrated between 10am–12pm. The closer the lot is to a popular put-in, the earlier it fills. Arriving after 11am on a Saturday usually means a long walk from a remote lot — or paying for an offsite shuttle.",
  },
  {
    q: "Is there a way to skip the River Road parking hunt entirely?",
    a: "Yes — book a cabana at Son's Guadalupe. Onsite parking is included with every cabana and glamping booking. You park steps from your cabana, the river, and the shuttle. No public lot fee, no walk, no shuttle line.",
  },
  {
    q: "Can you park free on River Road?",
    a: "There are no reliable free public lots near the popular tubing stretches. Roadside parking on River Road is heavily enforced during peak season — towing and tickets are common.",
  },
];

const schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
        { "@type": "ListItem", position: 3, name: TITLE, item: `https://sonsguadalupe.com${CANONICAL}` },
      ],
    },
    {
      "@type": "BlogPosting",
      headline: "River Road Parking Guide, New Braunfels — Lots, Fees & The Easy Way",
      description: DESCRIPTION,
      image: "https://sonsguadalupe.com/og-image.png",
      author: { "@type": "Organization", name: "Son's Guadalupe" },
      datePublished: "2026-05-30",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
      keywords:
        "River Road parking New Braunfels, Guadalupe River parking, where to park River Road, New Braunfels tubing parking, free parking River Road",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  ],
};

const BlogRiverRoadParkingGuide = () => {
  const related = getRelatedPosts(SLUG, 3);

  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={schemaJson} />
      <Header />

      <main>
        <article className="container-narrow mx-auto px-4 md:px-8 pt-28 pb-16">
          <BlogBreadcrumb title="River Road Parking Guide" />

          <header className="mb-8">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-charcoal mb-4">
              River Road Parking Guide, New Braunfels — Lots, Fees & The Easy Way
            </h1>
            <p className="flex items-center gap-2 text-sm text-charcoal/60">
              <CalendarDays className="w-4 h-4" /> Published {PUBLISH_DATE}
            </p>
          </header>

          <img
            src={heroImage}
            alt="Aerial view of River Road and Guadalupe River cabanas in New Braunfels"
            className="w-full h-auto rounded-2xl shadow-soft mb-10"
          />

          <div className="prose prose-lg max-w-none text-charcoal/80">
            <p className="text-xl leading-relaxed">
              River Road parking is the single most stressful part of a Guadalupe River day for most
              families. Here's a quick, no-nonsense breakdown of where to park, what it costs, and
              the option most locals quietly recommend: skip the public lots entirely.
            </p>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4 flex items-center gap-2">
              <MapPin className="w-6 h-6 text-teal" /> The 3 kinds of River Road parking
            </h2>
            <ul>
              <li>
                <strong>City of New Braunfels public lots</strong> — first-come, first-served. Fill
                early on summer weekends. Operated by the city; current rates are published on{" "}
                newbraunfels.gov.
              </li>
              <li>
                <strong>Private outfitter lots</strong> — bundled with tube rentals and shuttle
                fees. You park at the outfitter, ride a school bus to the put-in, float, and shuttle
                back to your car.
              </li>
              <li>
                <strong>Onsite riverside property parking</strong> — included with cabana and
                glamping bookings at Son's Guadalupe. You park <em>at</em> the river, next to your
                rental.
              </li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4 flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-teal" /> What does it cost?
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-charcoal/20">
                    <th className="py-3 pr-4">Option</th>
                    <th className="py-3 pr-4">Typical cost</th>
                    <th className="py-3">Walk to river</th>
                  </tr>
                </thead>
                <tbody className="[&_td]:py-3 [&_td]:pr-4 [&_tr]:border-b [&_tr]:border-charcoal/10">
                  <tr>
                    <td>City public lot</td>
                    <td>$10–$20/car/day</td>
                    <td>Varies, often 5–10 min walk</td>
                  </tr>
                  <tr>
                    <td>Outfitter lot + shuttle</td>
                    <td>Bundled with tube rental</td>
                    <td>Shuttle ride to put-in</td>
                  </tr>
                  <tr>
                    <td>Son's onsite parking</td>
                    <td>Included with cabana</td>
                    <td>Steps from your cabana</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-coral-orange" /> Don't park on the shoulder
            </h2>
            <p>
              Roadside parking on River Road is heavily enforced during peak season. Towing and
              tickets are common, and the walk to the river from a tow-friendly pull-off usually
              isn't worth the gamble.
            </p>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4 flex items-center gap-2">
              <Car className="w-6 h-6 text-teal" /> The locals' move: cabana + onsite parking
            </h2>
            <p>
              Every cabana and glamping booking at Son's Guadalupe includes a{" "}
              <strong>dedicated parking spot right at the river</strong>. No public lot fee. No
              shuttle from a remote lot. The float ends at your cabana, and your car is already
              there. For families especially, this is the easiest day on River Road.
            </p>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-12 mb-4">FAQs</h2>
            <div className="space-y-6">
              {FAQS.map((f) => (
                <div key={f.q}>
                  <h3 className="font-heading text-lg font-bold text-charcoal mb-2">{f.q}</h3>
                  <p>{f.a}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 grid sm:grid-cols-2 gap-4">
              <Link to="/book-now/sons-guadalupe">
                <Button variant="hero" size="lg" className="w-full">
                  Book a cabana (parking included)
                </Button>
              </Link>
              <Link to="/property-map">
                <Button variant="hero-outline" size="lg" className="w-full">
                  See the property map
                </Button>
              </Link>
            </div>
          </div>
        </article>

        <RelatedReads posts={related} />
      </main>

      <Footer />
    </div>
  );
};

export default BlogRiverRoadParkingGuide;
